import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import { Select, InputBase, Autocomplete, InputLabel } from "@mui/material";
import { MenuItem } from '@mui/material';

// Enhanced TextField with better contrast and styling
const lightPalette = {
  background: "#f9fafb", // Fondo gris muy claro (casi blanco)
  border: "#d1d5db",     // Borde gris claro
  hoverBg: "#f3f4f6",    // Fondo gris ligeramente más oscuro para hover
  focusBorder: "#a3a3a3", // Borde gris medio para el enfoque
  boxShadow: "0 0 0 2px rgba(163, 163, 163, 0.2)", // Sombra suave en focus
  inputColor: "#1f2937", // Texto de entrada gris oscuro
  placeholder: "#9ca3af", // Placeholder gris tenue
  labelColor: "#4b5563", // Etiqueta gris medio
  labelFocus: "#000", // Azul sutil para el enfoque de la etiqueta (opcional, puedes usar un gris fuerte)
  shrinkBg: "#ffffff",  // Fondo blanco para la etiqueta encogida
};

export const CustomTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  
  "& .MuiInputBase-root": {
    // CAMBIO: Fondo claro
    background: lightPalette.background,
    borderRadius: 12,
    // CAMBIO: Borde gris claro
    border: `1px solid ${lightPalette.border}`,
    padding: theme.spacing(1, 1.5),
    transition: "all 0.2s ease-in-out",
    
    // ESTADO HOVER
    "&:hover": {
      // CAMBIO: Borde y fondo en tonos de gris claros
      border: `1px solid ${lightPalette.focusBorder}`,
      background: lightPalette.hoverBg,
    },
    
    // ESTADO FOCUS
    "&.Mui-focused": {
      // CAMBIO: Borde y sombra sutil
      border: `1px solid ${lightPalette.focusBorder}`,
      boxShadow: lightPalette.boxShadow,
      background: lightPalette.background, // Mantenemos el fondo base en focus
    },
  },
  
  "& .MuiInputBase-input": {
    // CAMBIO: Color de entrada oscuro
    color: lightPalette.inputColor,
    fontSize: "0.95rem",
    padding: theme.spacing(1, 0.5),
    
    "&::placeholder": {
      // CAMBIO: Placeholder gris tenue
      color: lightPalette.placeholder,
      opacity: 1,
    },
  },
  
  "& .MuiFormLabel-root": {
    // CAMBIO: Color de etiqueta gris medio
    color: lightPalette.labelColor,
    fontSize: "0.9rem",
    fontWeight: 500,
    transform: "translate(12px, 16px) scale(1)",
  },
  
  "& .MuiFormLabel-root.Mui-focused": {
    // CAMBIO: Color de etiqueta en focus (usando azul sutil)
    color: lightPalette.labelFocus,
  },
  
  "& .MuiInputLabel-shrink": {
    transform: "translate(12px, -6px) scale(0.75)",
    // CAMBIO: Fondo blanco para la etiqueta encogida
    backgroundColor: lightPalette.shrinkBg, 
    padding: "0 5px",
  },
  
  "& .MuiOutlinedInput-notchedOutline": {
    // Esto permanece igual ya que usamos el borde manual
    border: "none",
  },
}));

// Enhanced Switch with better visual feedback
export const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 56,
  height: 34,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 7,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#ffffff",
      "& + .MuiSwitch-track": {
        backgroundColor: "rgba(90, 78, 153, 0.8)",
        opacity: 1,
        border: "1px solid rgba(150, 138, 213, 0.7)",
      },
      "& .MuiSwitch-thumb": {
        background: "linear-gradient(92deg, #968AD5 0%, #5A4E99 100%)",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 20,
    height: 20,
    borderRadius: 10,
    background: "#eeeeee",
    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.2)",
    transition: "all 0.2s ease",
  },
  "& .MuiSwitch-track": {
    borderRadius: 17,
    backgroundColor: "rgba(50, 50, 75, 0.8)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    opacity: 1,
    transition: "background-color 0.2s ease",
  },
}));

// Custom Select with matching style
export const CustomSelect = styled(Select)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  
  // 1. SELECT PRINCIPAL
  background: "#FFFFFF", // Fondo blanco sólido
  borderRadius: 12,
  // Borde oscuro para contraste, pero puedes ajustarlo
  border: "1px solid rgba(0, 0, 0, 0.3)", 
  transition: "all 0.2s ease-in-out",
  
  // 2. ESTADO HOVER (pasar el ratón por encima)
  "&:hover": {
    border: "1px solid rgba(0, 0, 0, 0.5)",
    background: "#F5F5F5", // Ligeramente gris para feedback
  },
  
  // 3. ESTADO FOCUSED (cuando está activo)
  "&.Mui-focused": {
    border: "1px solid #5A4E99", // Puedes mantener el color de énfasis
    boxShadow: "0 0 0 2px rgba(90, 78, 153, 0.25)",
    background: "#FFFFFF", // Fondo blanco
  },
  
  // 4. TEXTO DENTRO DEL SELECT
  "& .MuiSelect-select": {
    padding: "14px 16px",
    color: "#000000", // Color del texto negro
  },
  
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  
  // 5. ICONO DE LA FLECHA (SvgIcon)
  "& .MuiSvgIcon-root": {
    color: "rgba(0, 0, 0, 0.7)", // Color del icono oscuro
  },
  
  // 6. FONDO DEL MENÚ DESPLEGABLE (MuiMenu-paper)
  "& .MuiMenu-paper": {
    background: "#FFFFFF", // Fondo blanco sólido para el menú
    border: "1px solid rgba(0, 0, 0, 0.3)",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)", // Sombra más sutil
  },

  // 7. ESTILOS DE LAS OPCIONES (MenuItem)
  "& .MuiMenuItem-root": {
    color: "#000000", // Texto negro
    
    // Estado HOVER de la opción
    "&:hover": {
      background: "#EEEEEE", // Gris muy claro para feedback de hover
    },
    
    // Estado SELECTED de la opción
    "&.Mui-selected": {
      background: "#DDDDDD", // Gris claro para la opción seleccionada
      "&:hover": {
        background: "#CCCCCC", // Gris un poco más oscuro al hacer hover sobre la selección
      },
    },
  },
}));

// Additional styled InputBase for use with Select if needed
export const CustomInputBase = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    borderRadius: 12,
    position: "relative",
    backgroundColor: "rgba(15, 15, 40, 0.4)",
    border: "1px solid rgba(90, 78, 153, 0.3)",
    fontSize: 16,
    padding: "14px 16px",
    color: "#ffffff",
    transition: "all 0.2s ease-in-out",
    "&:focus": {
      borderRadius: 12,
      borderColor: "#5A4E99",
      boxShadow: "0 0 0 2px rgba(90, 78, 153, 0.25)",
      backgroundColor: "rgba(25, 25, 60, 0.6)",
    },
  },
}));

export const NoSuggestionsTextField = (props) => {
  return (
    <CustomTextField
      {...props}
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      autoComplete="off" // Set autocomplete off here
      inputProps={{
        autoComplete: "off", // And here
        form: {
          autoComplete: "off", // In case the form needs it
        },
        "aria-autocomplete": "none",
        role: "presentation",
        spellCheck: "false",
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiInputBase-root": {
    background: "rgba(15, 15, 40, 0.4)",
    borderRadius: 12,
    border: "1px solid rgba(90, 78, 153, 0.3)",
    padding: theme.spacing(1, 1.5),
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      border: "1px solid rgba(150, 138, 213, 0.5)",
      background: "rgba(20, 20, 50, 0.5)",
    },
    "&.Mui-focused": {
      border: "1px solid #5A4E99",
      boxShadow: "0 0 0 2px rgba(90, 78, 153, 0.25)",
      background: "rgba(25, 25, 60, 0.6)",
    },
  },
  "& .MuiInputBase-input": {
    color: "#ffffff",
    fontSize: "0.95rem",
    "&::placeholder": {
      color: "rgba(255, 255, 255, 0.5)",
      opacity: 1,
    },
  },
  "& .MuiFormLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "0.9rem",
    fontWeight: 500,
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: "#968AD5",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
}));

export function CustomAutocomplete({
  options,
  value,
  onChange,
  label,
  required = false,
  getOptionLabel = (option) => option.label || "",
  ...props
}) {
  return (
    <StyledAutocomplete
      disablePortal
      options={options}
      value={value}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id} 
      renderInput={(params) => (
        <CustomTextField {...params} label={label} required={required} />
      )}
      {...props}
    />
  );
}

export const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  background: '#FFFFFF',
  
  color: '#000000',
  
  '&:hover': {
    background: '#F0F0F0', 
    color: '#000000',
  },
  
  '&.Mui-selected': {
    background: '#E0E0E0', 
    color: '#000000',
    '&:hover': {
      background: '#D0D0D0', 
    },
  },
  
}));

export const CustomInputLabel = styled(InputLabel)(() => ({
  color: "#000000",

  "&.Mui-focused": {
    color: "#000000",
  },

  "&.MuiInputLabel-shrink": {
    color: "#000000",
  },

  "&.MuiInputLabel-shrink.Mui-focused": {
    color: "#000000",
  },
}));